import type {
  BillingResponse,
  GenerateRequest,
  GenerateResponse,
  GenerationLogItem,
  OAuthStatus,
} from "../types";
import {
  armJobEventTimeout,
  ensureEventChannel,
  subscribeToJob,
} from "./eventChannel";

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  const data = (await res.json().catch(() => ({}))) as T & {
    error?: string | { code?: string; message?: string };
    currentVersion?: number;
    attempts?: unknown;
  };
  if (!res.ok) {
    const raw = (data as { error?: string | { code?: string; message?: string } })
      .error;
    const message =
      typeof raw === "string"
        ? raw
        : raw?.message ?? `Request failed: ${res.status}`;
    const err = new Error(message) as Error & {
      status?: number;
      code?: string;
      currentVersion?: number;
    };
    err.status = res.status;
    if (typeof raw !== "string" && raw?.code) err.code = raw.code;
    // Some routes return { error: "...", code: "USAGE_LIMIT" } as flat fields
    // alongside a string error. Pick that up too so callers can branch on
    // err.code without parsing the message.
    if (!err.code) {
      const flatCode = (data as { code?: unknown }).code;
      if (typeof flatCode === "string") err.code = flatCode;
    }
    if (typeof data.currentVersion === "number") {
      err.currentVersion = data.currentVersion;
    }
    console.warn(
      `[ima2][api] ${init?.method ?? "GET"} ${url} failed: status=${res.status} ` +
      `code=${err.code ?? "?"} msg=${message}`,
      Array.isArray(data.attempts) ? { attempts: data.attempts } : undefined,
    );
    throw err;
  }
  return data;
}

export type InflightJob = {
  requestId: string;
  kind: string;
  prompt: string;
  startedAt: number;
  status?: "queued" | "running" | "succeeded" | "failed" | "canceled";
  queueStatus?: "queued" | "running" | "succeeded" | "failed" | "canceled";
  phase?: string;
  phaseAt?: number;
  attempt?: number;
  maxAttempts?: number;
  meta?: Record<string, unknown>;
  filename?: string | null;
  errorMessage?: string | null;
};

export function getInflight(params?: {
  kind?: "classic" | "node";
  sessionId?: string;
}): Promise<{ jobs: InflightJob[] }> {
  const qs = new URLSearchParams();
  if (params?.kind) qs.set("kind", params.kind);
  if (params?.sessionId) qs.set("sessionId", params.sessionId);
  const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
  return jsonFetch(`/api/inflight${suffix}`);
}

export async function cancelInflight(requestId: string): Promise<void> {
  await fetch(`/api/inflight/${encodeURIComponent(requestId)}`, {
    method: "DELETE",
  }).catch(() => {});
}

export function getOAuthStatus(): Promise<OAuthStatus> {
  return jsonFetch<OAuthStatus>("/api/oauth/status");
}

export function getBilling(): Promise<BillingResponse> {
  return jsonFetch<BillingResponse>("/api/billing");
}

// Hard ceiling on a single /api/generate or /api/edit POST. Server's
// runResponses caps the upstream stream at 5 min; we add a 90-second
// margin so a server-side timeout fires before our client one (lets the
// server clean up inflight rows + stream a structured error). Without
// any timeout here a wedged proxy could hang the txt-batch chunk loop
// forever (production: 2026-04-30 batch stuck on attempt 4 streaming).
const GENERATE_REQUEST_TIMEOUT_MS = 6.5 * 60 * 1000;

export function postGenerate(payload: GenerateRequest): Promise<GenerateResponse> {
  return jsonFetch<GenerateResponse>("/api/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal: AbortSignal.timeout(GENERATE_REQUEST_TIMEOUT_MS),
  });
}

export async function postGenerateWithProgress(
  payload: GenerateRequest,
  handlers: {
    onPartial?: (partial: { image: string; index?: number | null }) => void;
    onPhase?: (phase: string) => void;
  } = {},
): Promise<GenerateResponse> {
  if (!payload.requestId || !payload.partialImages) return postGenerate(payload);
  const readinessController = new AbortController();
  await ensureEventChannel(readinessController.signal);
  const unsubscribe = subscribeToJob(payload.requestId, (event, data) => {
    if (event === "partial" && typeof data.image === "string") {
      handlers.onPartial?.({
        image: data.image,
        index: typeof data.index === "number" ? data.index : null,
      });
    } else if (event === "phase" && typeof data.phase === "string") {
      handlers.onPhase?.(data.phase);
    }
  });
  try {
    return await postGenerate(payload);
  } finally {
    readinessController.abort();
    unsubscribe();
  }
}

export type GenerateQueueResponse = {
  queued: true;
  requestId: string;
  status: "queued";
};

export function postGenerateQueued(payload: GenerateRequest): Promise<GenerateQueueResponse> {
  return jsonFetch<GenerateQueueResponse>("/api/generate/queue", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...payload, n: 1 }),
  });
}

export type BatchSummary = {
  total: number;
  succeeded: number;
  failed: number;
  totalAttempts: number;
  totalUsage: Record<string, number> | null;
  reasons: Record<string, number>;
};

export type BatchCloseResponse = {
  meta: Record<string, unknown> | null;
  summary: BatchSummary;
};

export function closeBatch(
  batchId: string,
  stopReason?: string | null,
): Promise<BatchCloseResponse> {
  return jsonFetch<BatchCloseResponse>(
    `/api/batch/${encodeURIComponent(batchId)}/close`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(stopReason ? { stopReason } : {}),
    },
  );
}

export function getBatch(batchId: string): Promise<{
  meta: Record<string, unknown> | null;
  summary: BatchSummary;
  entries: Array<Record<string, unknown>>;
}> {
  return jsonFetch(`/api/batch/${encodeURIComponent(batchId)}`);
}

export function listBatches(limit = 50): Promise<{
  batches: Array<Record<string, unknown>>;
}> {
  return jsonFetch(`/api/batch?limit=${limit}`);
}

export function postEdit(payload: GenerateRequest): Promise<GenerateResponse> {
  return jsonFetch<GenerateResponse>("/api/edit", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    signal: AbortSignal.timeout(GENERATE_REQUEST_TIMEOUT_MS),
  });
}

export async function postEditWithProgress(
  payload: GenerateRequest,
  handlers: {
    onPartial?: (partial: { image: string; index?: number | null }) => void;
    onPhase?: (phase: string) => void;
  } = {},
): Promise<GenerateResponse> {
  if (!payload.requestId || !payload.partialImages) return postEdit(payload);
  const readinessController = new AbortController();
  await ensureEventChannel(readinessController.signal);
  const unsubscribe = subscribeToJob(payload.requestId, (event, data) => {
    if (event === "partial" && typeof data.image === "string") {
      handlers.onPartial?.({ image: data.image, index: typeof data.index === "number" ? data.index : null });
    } else if (event === "phase" && typeof data.phase === "string") {
      handlers.onPhase?.(data.phase);
    }
  });
  try {
    return await postEdit(payload);
  } finally {
    readinessController.abort();
    unsubscribe();
  }
}

export type HistoryItem = {
  filename: string;
  url: string;
  thumb?: string;
  web?: string;
  createdAt: number;
  prompt: string | null;
  originalPrompt?: string | null;
  systemPrompt?: string | null;
  systemPromptEnabled?: boolean;
  promptRuntime?: import("../types").PromptRuntime | null;
  quality: string | null;
  size: string | null;
  width?: number | null;
  height?: number | null;
  resolution?: string | null;
  moderation?: string | null;
  format: string;
  background?: string | null;
  compression?: number | null;
  responseId?: string | null;
  imageCallId?: string | null;
  previousResponseId?: string | null;
  provider: string;
  imageRoute?: string | null;
  imageModel?: string | null;
  responsesModel?: string | null;
  codexAccount?: string | null;
  usage: Record<string, unknown> | null;
  webSearchCalls: number;
  sessionId?: string | null;
  nodeId?: string | null;
  parentNodeId?: string | null;
  clientNodeId?: string | null;
  kind?: string | null;
  favorite?: boolean;
  /** echoes the requestId that produced this image, when known. Lets the
   *  client reconcile a pending in-flight entry after a refresh by matching
   *  history rows against `inFlight[].id`. */
  requestId?: string | null;
  /** Reference-image lineage: which images were used as visual references
   *  when this image was generated. Empty for unconditional generations. */
  references?: import("../types").ReferenceImageRef[];
};

export type HistoryCursor = { before: number; beforeFilename: string };

export type HistoryPage = {
  items: HistoryItem[];
  total: number;
  nextCursor: HistoryCursor | null;
};

export type HistorySessionGroup = {
  sessionId: string;
  items: HistoryItem[];
  lastUsedAt: number;
};

export type HistoryGroupedPage = {
  sessions: HistorySessionGroup[];
  loose: HistoryItem[];
  total: number;
  nextCursor: HistoryCursor | null;
};

export function getHistory(
  params: { limit?: number; since?: number; cursor?: HistoryCursor; sessionId?: string } = {},
): Promise<HistoryPage> {
  const qs = new URLSearchParams();
  qs.set("limit", String(params.limit ?? 50));
  if (params.since != null) qs.set("since", String(params.since));
  if (params.cursor) {
    qs.set("before", String(params.cursor.before));
    qs.set("beforeFilename", params.cursor.beforeFilename);
  }
  if (params.sessionId) qs.set("sessionId", params.sessionId);
  return jsonFetch(`/api/history?${qs.toString()}`);
}

export function getHistoryGrouped(
  params: { limit?: number; cursor?: HistoryCursor } = {},
): Promise<HistoryGroupedPage> {
  const qs = new URLSearchParams();
  qs.set("groupBy", "session");
  qs.set("limit", String(params.limit ?? 200));
  if (params.cursor) {
    qs.set("before", String(params.cursor.before));
    qs.set("beforeFilename", params.cursor.beforeFilename);
  }
  return jsonFetch(`/api/history?${qs.toString()}`);
}

export function deleteHistoryItem(filename: string): Promise<{
  ok: boolean;
  trashId: string;
  filename: string;
  unlinkAt: number;
  sessionsTouched: number;
  nodesTouched: number;
}> {
  return jsonFetch(`/api/history/${encodeURIComponent(filename)}`, { method: "DELETE" });
}

export function restoreHistoryItem(filename: string, trashId: string): Promise<{ ok: boolean }> {
  return jsonFetch(`/api/history/${encodeURIComponent(filename)}/restore`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ trashId }),
  });
}

export async function setFavorite(filename: string, value: boolean): Promise<{ filename: string; favorite: boolean }> {
  const res = await fetch(
    `/api/history/${encodeURIComponent(filename)}/favorite`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ value }),
    },
  );
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || `setFavorite failed: ${res.status}`);
  }
  return res.json();
}

export type NodeGenerateRequest = {
  parentNodeId: string | null;
  prompt: string;
  quality: string;
  size: string;
  format: string;
  moderation: "low" | "auto";
  provider?: "oauth";
  references?: string[];
  requestId?: string;
  sessionId?: string | null;
  clientNodeId?: string | null;
  maxAttempts?: number;
  originalPrompt?: string;
  systemPrompt?: string;
  includeSystemPrompt?: boolean;
};

export type NodeGenerateResponse = {
  nodeId: string;
  parentNodeId: string | null;
  image: string;
  filename: string;
  url: string;
  thumb?: string;
  web?: string;
  elapsed: number;
  usage?: { total_tokens?: number } & Record<string, unknown>;
  webSearchCalls: number;
  provider: "oauth";
  moderation?: string;
  // Echoed by /api/node/generate so the UI can preserve the preview aspect
  // ratio without re-deriving from the node sidecar.
  size?: string | null;
  systemPrompt?: string | null;
  systemPromptEnabled?: boolean;
};

export type NodeErrorResponse = {
  error: { code: string; message: string };
  parentNodeId: string | null;
};

export type NodeImportHistoryRequest = {
  historyFilename: string;
  sessionId?: string | null;
  clientNodeId?: string | null;
};

export type NodeImportHistoryResponse = {
  nodeId: string;
  filename: string;
  url: string;
  prompt: string;
  size: string | null;
  importedFromFilename: string;
};

export async function postNodeImportHistory(
  payload: NodeImportHistoryRequest,
): Promise<NodeImportHistoryResponse> {
  const res = await fetch("/api/node/import-history", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = data as { error?: { code?: string; message?: string } };
    const msg = err?.error?.message ?? `Request failed: ${res.status}`;
    const e = new Error(msg) as Error & { code?: string };
    e.code = err?.error?.code;
    throw e;
  }
  return data as NodeImportHistoryResponse;
}

export async function postNodeGenerate(payload: NodeGenerateRequest): Promise<NodeGenerateResponse> {
  const res = await fetch("/api/node/generate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = data as NodeErrorResponse;
    const msg = err?.error?.message ?? `Request failed: ${res.status}`;
    const e = new Error(msg) as Error & { code?: string };
    e.code = err?.error?.code;
    throw e;
  }
  return data as NodeGenerateResponse;
}

// Streaming variant of postNodeGenerate. The server emits `phase` (queue),
// `partial` (progressive image preview), `done` (final payload identical to
// the non-streaming response), or `error` events. If the proxy decides not
// to upgrade to SSE we fall back to the JSON shape.
export async function postNodeGenerateStream(
  payload: NodeGenerateRequest,
  handlers: {
    onPartial?: (partial: { image: string; requestId?: string | null; index?: number | null }) => void;
    onPhase?: (phase: { phase?: string; requestId?: string | null }) => void;
  } = {},
): Promise<NodeGenerateResponse> {
  const requestId = payload.requestId ?? `nreq_${crypto.randomUUID()}`;
  const EVENT_CHANNEL_READY_GRACE_MS = 3_000;
  const RESULT_RECONCILE_MS = 2_000;

  return new Promise<NodeGenerateResponse>((resolve, reject) => {
    let settled = false;
    let reconcileHandle: ReturnType<typeof setInterval> | null = null;
    const readinessController = new AbortController();
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      readinessController.abort();
      if (reconcileHandle) clearInterval(reconcileHandle);
      reconcileHandle = null;
      clearTimeoutHandle();
      unsubscribe();
      callback();
    };
    const recoverTerminalResult = async () => {
      try {
        const result = await getNodeResult(requestId);
        if (result?.status === "done") {
          finish(() => resolve(result.payload));
        } else if (result?.status === "error") {
          const error = new Error(result.error.message) as Error & { code?: string };
          error.code = result.error.code;
          finish(() => reject(error));
        }
      } catch {
        // Normal reconciliation and timeout handling remain the fallback.
      }
    };
    const unsubscribe = subscribeToJob(requestId, (event, data) => {
      if (event === "partial" && typeof data.image === "string") {
        handlers.onPartial?.(data as { image: string; requestId?: string | null; index?: number | null });
      } else if (event === "phase") {
        handlers.onPhase?.(data as { phase?: string; requestId?: string | null });
      } else if (event === "done") {
        if (typeof data.image === "string") {
          finish(() => resolve(data as unknown as NodeGenerateResponse));
        } else {
          void recoverTerminalResult();
        }
      } else if (event === "error") {
        const response = data as unknown as NodeErrorResponse;
        const error = new Error(response?.error?.message ?? "Node generation failed") as Error & { code?: string };
        error.code = response?.error?.code;
        finish(() => reject(error));
      }
    });
    const clearTimeoutHandle = armJobEventTimeout(() => {
      void recoverTerminalResult().finally(() => {
        if (!settled) finish(() => reject(new Error("Node generation event stream timed out")));
      });
    });

    const waitForEventChannelGrace = () => new Promise<void>((resolveReady, rejectReady) => {
      const graceTimer = setTimeout(resolveReady, EVENT_CHANNEL_READY_GRACE_MS);
      void ensureEventChannel(readinessController.signal).then(() => {
        clearTimeout(graceTimer);
        resolveReady();
      }).catch((error) => {
        clearTimeout(graceTimer);
        rejectReady(error);
      });
    });

    void waitForEventChannelGrace().then(() => {
      if (settled) return null;
      return fetch("/api/node/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload, requestId, async: true }),
      });
    }).then(async (res) => {
      if (!res) return;
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const response = data as NodeErrorResponse;
        const error = new Error(response?.error?.message ?? `Request failed: ${res.status}`) as Error & { code?: string };
        error.code = response?.error?.code;
        finish(() => reject(error));
        return;
      }
      // Rolling-deploy compatibility: an older server ignores `async` and
      // returns the completed node payload with HTTP 200.
      if (res.status === 200 && typeof (data as NodeGenerateResponse).image === "string") {
        finish(() => resolve(data as NodeGenerateResponse));
      } else if (res.status === 202 && !settled) {
        void recoverTerminalResult();
        reconcileHandle = setInterval(() => void recoverTerminalResult(), RESULT_RECONCILE_MS);
      }
    }).catch((error) => {
      if (settled && (error as Error).name === "AbortError") return;
      finish(() => reject(error instanceof Error ? error : new Error(String(error))));
    });
  });
}

// ── Sessions (0.06) ──
export type SessionSummary = {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  graphVersion: number;
  nodeCount: number;
};

export type SessionGraphNode = {
  id: string;
  x: number;
  y: number;
  data: Record<string, unknown>;
};
export type SessionGraphEdge = {
  id: string;
  source: string;
  target: string;
  data: Record<string, unknown>;
};
export type SessionFull = {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  graphVersion: number;
  nodes: SessionGraphNode[];
  edges: SessionGraphEdge[];
};

export function listSessions(): Promise<{ sessions: SessionSummary[] }> {
  return jsonFetch("/api/sessions");
}
export function createSession(title: string): Promise<{ session: SessionSummary }> {
  return jsonFetch("/api/sessions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title }),
  });
}
export function getSession(id: string): Promise<{ session: SessionFull }> {
  return jsonFetch(`/api/sessions/${encodeURIComponent(id)}`);
}
export function renameSession(id: string, title: string): Promise<{ ok: boolean }> {
  return jsonFetch(`/api/sessions/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title }),
  });
}
export function deleteSession(id: string): Promise<{ ok: boolean }> {
  return jsonFetch(`/api/sessions/${encodeURIComponent(id)}`, { method: "DELETE" });
}
export type GraphSaveMeta = {
  saveId?: string;
  saveReason?: string;
  tabId?: string;
};

export function saveSessionGraph(
  id: string,
  graphVersion: number,
  nodes: SessionGraphNode[],
  edges: SessionGraphEdge[],
  meta: GraphSaveMeta = {},
): Promise<{ ok: boolean; nodes: number; edges: number; graphVersion: number }> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "If-Match": String(graphVersion),
  };
  if (meta.saveId) headers["X-Ima2-Graph-Save-Id"] = meta.saveId;
  if (meta.saveReason) headers["X-Ima2-Graph-Save-Reason"] = meta.saveReason;
  if (meta.tabId) headers["X-Ima2-Tab-Id"] = meta.tabId;
  return jsonFetch(`/api/sessions/${encodeURIComponent(id)}/graph`, {
    method: "PUT",
    headers,
    body: JSON.stringify({ nodes, edges }),
  });
}


export type ReconcileOrphansResult = {
  ok: boolean;
  recovered: number;
  stalified: number;
  scanned: { success: number; failed: number; brokenBefore: number };
  graphVersion: number;
};

export function reconcileOrphans(id: string): Promise<ReconcileOrphansResult> {
  return jsonFetch(`/api/sessions/${encodeURIComponent(id)}/reconcile-orphans`, {
    method: "POST",
  });
}

// Step 4-B: client polls this when the streaming response was lost. Returns
// the cached generation result (status: "done" with payload, or "error" with
// error info) for up to 24h after generation completes. 404 means generation
// is still in flight or TTL has evicted the entry.
export type NodeResultDone = {
  status: "done";
  clientNodeId: string | null;
  sessionId: string | null;
  payload: NodeGenerateResponse;
  savedAt: number;
};
export type NodeResultError = {
  status: "error";
  clientNodeId: string | null;
  sessionId: string | null;
  error: { code: string; message: string; status: number };
  savedAt: number;
};
export type NodeResult = NodeResultDone | NodeResultError;

export async function getNodeResult(requestId: string): Promise<NodeResult | null> {
  try {
    return await jsonFetch(`/api/node/result/${encodeURIComponent(requestId)}`);
  } catch (err) {
    const status = (err as { status?: number }).status;
    if (status === 404) return null;
    throw err;
  }
}

export function getGenerationLog(
  params: { limit?: number; status?: "success" | "failed" } = {},
): Promise<{ items: GenerationLogItem[]; total: number }> {
  const qs = new URLSearchParams();
  qs.set("limit", String(params.limit ?? 100));
  if (params.status) qs.set("status", params.status);
  return jsonFetch(`/api/generation-log?${qs.toString()}`);
}

// Resolve the server-side failure record for an activity-log entry (the client
// keeps only a thin localStorage row). Returns null when there is no sidecar —
// e.g. the failure never reached the server, or it was already deleted.
export async function getFailedLogByRequestId(
  requestId: string,
): Promise<GenerationLogItem | null> {
  try {
    const res = await jsonFetch<{ item: GenerationLogItem }>(
      `/api/generation-log/failed/by-request/${encodeURIComponent(requestId)}`,
    );
    return res.item ?? null;
  } catch (err) {
    if ((err as { status?: number })?.status === 404) return null;
    throw err;
  }
}

export function deleteFailedLogItem(id: string): Promise<{ ok: boolean; id: string }> {
  const clean = id.replace(/^failed\//, "");
  return jsonFetch(`/api/generation-log/failed/${encodeURIComponent(clean)}`, {
    method: "DELETE",
  });
}

export async function enhancePrompt(
  prompt: string,
  language: "ko" | "en" = "ko",
  references: string[] = [],
): Promise<{ prompt: string }> {
  const payload: Record<string, unknown> = { prompt, language };
  if (references.length > 0) payload.references = references;
  const res = await fetch("/api/enhance-prompt", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(body || `enhancePrompt failed: ${res.status}`);
  }
  return res.json();
}

// Phase 6.3 — Prompt library
export type PromptItem = {
  id: string;
  title: string;
  body: string;
  pinned: boolean;
  useCount: number;
  lastUsedAt: number | null;
  createdAt: number;
  updatedAt: number;
  owner: string | null;
  // fork extension: GitHub import 출처 추적 (PromptLibraryModal 카드에 표기).
  source?: string | null;
  sourceUrl?: string | null;
};

export function listPrompts(q = ""): Promise<{ items: PromptItem[] }> {
  const qs = new URLSearchParams();
  if (q.trim()) qs.set("q", q.trim());
  qs.set("limit", "200");
  return jsonFetch(`/api/prompts?${qs.toString()}`);
}

export function createPrompt(
  title: string,
  body: string,
): Promise<{ item: PromptItem }> {
  return jsonFetch("/api/prompts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ title, body }),
  });
}

export function updatePrompt(
  id: string,
  patch: { title?: string; body?: string; pinned?: boolean },
): Promise<{ item: PromptItem }> {
  return jsonFetch(`/api/prompts/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
}

export function deletePrompt(id: string): Promise<{ ok: true }> {
  return jsonFetch(`/api/prompts/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function bumpPromptUse(
  id: string,
): Promise<{ useCount: number; lastUsedAt: number }> {
  return jsonFetch(`/api/prompts/${encodeURIComponent(id)}/use`, {
    method: "POST",
  });
}

// fork extension: GitHub URL 의 fenced code block 들을 prompts 라이브러리에
// source=github 로 일괄 저장.
export type PromptImportResult = {
  sourceUrl: string;
  created: PromptItem[];
  skipped: Array<{ title: string; error: string }>;
};

export function importPromptsFromGitHub(url: string): Promise<PromptImportResult> {
  return jsonFetch("/api/prompts/import-github", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
  });
}
